import { useEffect, useRef, useState } from 'react'

import analyticsJson from '../../demo/fixtures/analytics.json'
import copyJson from '../../demo/fixtures/copy.json'
import enumsJson from '../../demo/fixtures/enums.json'
import type { AnalyticsLens, AnalyticsRangeKey } from '../../demo/types'
import { Button, Icon } from '../../ui'
import styles from './Controls.module.css'

interface RangeOption {
  key: AnalyticsRangeKey
  label: string
  dateLabel: string
}

interface AnalyticsFixture {
  ranges: Record<AnalyticsRangeKey, RangeOption>
}

interface EnumFixture {
  objective: Record<AnalyticsLens, string>
}

interface ControlsProps {
  range: AnalyticsRangeKey
  lens: AnalyticsLens
  compare: boolean
  onRangeChange: (range: AnalyticsRangeKey) => void
  onLensChange: (lens: AnalyticsLens) => void
  onCompareChange: (compare: boolean) => void
}

const fixture = analyticsJson as unknown as AnalyticsFixture
const enumFixture = enumsJson as unknown as EnumFixture
const rangeOptions = Object.values(fixture.ranges)

export function Controls({ range, lens, compare, onRangeChange, onLensChange, onCompareChange }: ControlsProps) {
  const [rangeOpen, setRangeOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const currentRange = fixture.ranges[range]

  useEffect(() => {
    if (!rangeOpen) return
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setRangeOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [rangeOpen])

  return (
    <div className={styles.controls} data-scope="analytics-controls">
      <div className={styles.segment} aria-label="数据口径">
        {(['sales', 'awareness'] as const).map((item) => (
          <button
            className={`${styles.segmentButton} ${lens === item ? styles.segmentActive : ''}`}
            key={item}
            type="button"
            aria-pressed={lens === item}
            onClick={() => onLensChange(item)}
          >
            {enumFixture.objective[item]}
          </button>
        ))}
      </div>
      <div className={styles.rangeRoot} ref={rootRef}>
        <Button
          aria-expanded={rangeOpen}
          aria-haspopup="listbox"
          className={styles.rangeButton}
          variant="ghost"
          onClick={() => setRangeOpen((open) => !open)}
        >
          <Icon name="calendar" size={18} />
          <span>{currentRange.label}</span>
          <span className={styles.dateLabel}>{currentRange.dateLabel}</span>
          <Icon name="chevron-down" size={16} />
        </Button>
        {rangeOpen ? (
          <div className={styles.rangeMenu} role="listbox" aria-label="日期区间">
            {rangeOptions.map((item) => (
              <button
                className={`${styles.rangeOption} ${range === item.key ? styles.rangeOptionActive : ''}`}
                key={item.key}
                type="button"
                role="option"
                aria-selected={range === item.key}
                onClick={() => {
                  onRangeChange(item.key)
                  setRangeOpen(false)
                }}
              >
                <span>{item.label}</span>
                <span>{item.dateLabel}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <Button
        aria-pressed={compare}
        className={`${styles.compareButton} ${compare ? styles.compareActive : ''}`}
        variant="ghost"
        onClick={() => onCompareChange(!compare)}
      >
        <Icon name="compare" size={18} />
        <span>{copyJson.labels.compare}</span>
      </Button>
    </div>
  )
}
